import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { ConfigurationService } from '../../services/configuration.service';

export interface SetupBrickPercent { brickId: number; brickName?: string; percent: number; }
export interface SetupBuPercent { businessUnitId: number; businessUnitName?: string; percent: number; defaultPercent?: number; }
export interface SetupProduct { itemId?: number; itemBrandId?: number; name?: string; }
export interface SetupBrickSplit { start: string; end?: string; bricks: SetupBrickPercent[]; }
export interface SetupProductSplit extends SetupBrickSplit { product: SetupProduct; ids: number[]; }
export interface SetupTerritorySplit {
  distributorBrickId?: number; distributorBrickCode?: string; product?: SetupProduct;
  brickId: number; brickName: string; lineId: number; lineName: string;
  start: string; end?: string; shares: SetupBuPercent[]; ids: number[];
}
export interface DistributorBrickSetup {
  id: number; code: string; name: string; distributorId: number;
  brickSplits: SetupBrickSplit[]; productSplits: SetupProductSplit[]; territorySplits: SetupTerritorySplit[];
}
export interface SetupHolder { businessUnitId: number; businessUnitName: string; lineId: number; lineName: string; percent: number; }
/** brands have negative ids (-brandId), items positive */
export interface SetupProductOption { id: number; name: string; itemBrandId?: number; }
export interface SheetIssue { row: number; code: string; message: string; }
/** the configured names of the business unit level holding the sales bricks (e.g. Territory) and of the lines */
export interface LevelNames { holder: string; holders: string; line: string; lines: string; }
export const DEFAULT_LEVEL_NAMES: LevelNames = { holder: 'Business unit', holders: 'Business units', line: 'Line', lines: 'Lines' };
export interface SharedBrick { lineId: number; lineName: string; brickId: number; brickName: string; holders: string[]; }
export interface BrickSharePeriod { start: string; end?: string; onlyHolder: boolean; shares: SetupBuPercent[]; }

/** yyyy-MM-dd of a date picked in the browser (its local day) */
export function dayText(d: any): string {
  if (!d) return null;
  if (typeof d === 'string') return d.substring(0, 10);
  const pad = (n: number) => (n < 10 ? '0' : '') + n;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** a yyyy-MM-dd(THH:mm) text as a local date for the date pickers */
export function dayDate(text: string): Date {
  if (!text) return null;
  const [y, m, d] = text.substring(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function apiError(err: any, fallback: string): string {
  const body = err && err.error;
  if (body && typeof body === 'object' && (body.Message || body.message)) return body.Message || body.message;
  if (typeof body === 'string' && body.length < 500) return body;
  return fallback;
}

/** Distributor bricks setup (brick / product / business unit splits, setup sheet), shared bricks' dated shares and Distributor Target sheets. */
@Injectable({ providedIn: 'root' })
export class DistributorSetupService {
  constructor(private http: HttpClient, private configurations: ConfigurationService) { }

  private get api() { return this.configurations.apiAddress + '/api/DistributorBrickSetup'; }

  private names$: Observable<LevelNames>;
  /** loaded once per session */
  levelNames(): Observable<LevelNames> {
    if (!this.names$) this.names$ = this.http.get<LevelNames>(`${this.api}/levelNames`).pipe(shareReplay(1));
    return this.names$;
  }

  sharedBricks(date: string, lineId?: number) {
    let params = new HttpParams().set('date', date);
    if (lineId) params = params.set('lineId', String(lineId));
    return this.http.get<SharedBrick[]>(`${this.api}/sharedBricks`, { params });
  }
  brickShares(brickId: number, lineId: number) {
    return this.http.get<BrickSharePeriod[]>(`${this.api}/brickShares`, { params: new HttpParams().set('brickId', String(brickId)).set('lineId', String(lineId)) });
  }
  saveBrickShares(body: any) { return this.http.post(`${this.api}/brickShares`, body); }

  getSetup(distributorBrickId: number) { return this.http.get<DistributorBrickSetup>(`${this.api}/setup/${distributorBrickId}`); }
  holders(brickId: number, date: string) { return this.http.get<SetupHolder[]>(`${this.api}/holders`, { params: new HttpParams().set('brickId', String(brickId)).set('date', date) }); }
  products() { return this.http.get<SetupProductOption[]>(`${this.api}/products`); }
  brickProductSplits(brickId: number, lineId: number) {
    return this.http.get<SetupTerritorySplit[]>(`${this.api}/brickProductSplits`, { params: new HttpParams().set('brickId', String(brickId)).set('lineId', String(lineId)) });
  }
  saveBrickSplit(distributorBrickId: number, body: any) { return this.http.post(`${this.api}/brickSplit/${distributorBrickId}`, body); }
  saveProductSplit(distributorBrickId: number, body: any) { return this.http.post(`${this.api}/productSplit/${distributorBrickId}`, body); }
  deleteProductSplit(ids: number[]) { return this.http.post(`${this.api}/deleteProductSplit`, ids); }
  saveTerritorySplit(body: any) { return this.http.post(`${this.api}/territorySplit`, body); }
  deleteTerritorySplit(ids: number[], forCode: boolean) { return this.http.post(`${this.api}/deleteTerritorySplit?forCode=${forCode}`, ids); }

  uploadSheet(file: File, distributorId: number, checkOnly: boolean, defaultStartDate: string, brickLevelId: number, updateNames: boolean) {
    const form = new FormData();
    form.append(file.name, file);
    let params = new HttpParams().set('distributorId', String(distributorId)).set('checkOnly', String(checkOnly)).set('updateNames', String(updateNames));
    if (defaultStartDate) params = params.set('defaultStartDate', defaultStartDate);
    if (brickLevelId) params = params.set('brickLevelId', String(brickLevelId));
    // runs in the background: an HttpEvent stream answering { jobId } (follow it with UploadJobService.run)
    return this.http.request(new HttpRequest('POST', `${this.api}/uploadSheet`, form, { params, reportProgress: true }));
  }
  setupUploads(distributorId: number) { return this.http.get<any[]>(`${this.api}/setupUploads`, { params: new HttpParams().set('distributorId', String(distributorId)) }); }
  setupUploadFile(uploadId: number) { return this.http.get(`${this.api}/setupUploadFile/${uploadId}`, { responseType: 'blob' }); }
  exportSheet(distributorId: number, date: string) {
    return this.http.get(`${this.api}/exportSheet`, { params: new HttpParams().set('distributorId', String(distributorId)).set('date', date), responseType: 'blob' });
  }
  template() { return this.http.get(`${this.api}/template`, { responseType: 'blob' }); }

  uploadTargets(file: File, distributorId: number, date: string, checkOnly: boolean) {
    const form = new FormData();
    form.append(file.name, file);
    let params = new HttpParams().set('checkOnly', String(checkOnly));
    if (distributorId) params = params.set('distributorId', String(distributorId));
    if (date) params = params.set('date', date);
    // runs in the background: an HttpEvent stream answering { jobId } (follow it with UploadJobService.run)
    return this.http.request(new HttpRequest('POST', `${this.api}/uploadTargets`, form, { params, reportProgress: true }));
  }
  targetTemplate() { return this.http.get(`${this.api}/targetTemplate`, { responseType: 'blob' }); }
}
